"use client";

import { c as _c } from "react-compiler-runtime";
import { Loader2 } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { Text } from "@/components/primitives";
import AppImage from "@/components/primitives/Image/Image";
import { useAnalytics } from "@/core/analytics";
import { queryClient } from "@/core/caching";
import { useTranslation } from "@/core/i18n";
import { logger } from "@/core/logging";
import type { DisplayAgelessAssessment } from "@/core/api/orval/model";
import { useAssessmentStep } from "@/features/assessments/AssessmentStepProvider/AssessmentStepProvider";
import { uploadFrontalFacialImageForAssessment } from "@/features/assessments/components/ImageCaptureStep/hooks/photoApi";
import { CaptureHeader } from "@/features/assessments/components/ImageCaptureStep/stages/components/CaptureHeader";
import { fetchDemoImageAsFile, LASH_BROW_DEMO_PERSONAS } from "@/features/assessments/demo/demoPersonas";
import { DemoPersonaSelector } from "@/features/assessments/demo/DemoPersonaSelector";
import { getRetrieveCurrentAssessmentQueryKey } from "@orval/ageless-assessments/ageless-assessments";
import { setAssessmentPhotoCaptureComplete } from "@orval/ageless-imaging/ageless-imaging";
type LashBrowDemoCaptureStageProps = {
  assessment: DisplayAgelessAssessment;
  headers: HeadersInit;
  onComplete: () => void;
};
type DemoCapturePhase = "pickPersona" | "uploading" | "error";

// Demo-mode replacement for the live-camera lash/brow capture: uploads a
// bundled persona photo as the frontal facial image. The eye-region crop is
// intentionally skipped — the backend falls back to the full frontal image.
export function LashBrowDemoCaptureStage({
  assessment,
  headers,
  onComplete
}: LashBrowDemoCaptureStageProps): React.ReactElement {
  const {
    t
  } = useTranslation();
  const analytics = useAnalytics();
  const {
    flowType
  } = useAssessmentStep();
  const [phase, setPhase] = useState<DemoCapturePhase>("pickPersona");
  const [selectedPersonaId, setSelectedPersonaId] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const demoThumbnails = LASH_BROW_DEMO_PERSONAS.map(persona => ({
    id: persona.id,
    label: persona.label,
    thumbnailUrl: persona.frontImageUrl
  }));
  const selectedPersona = selectedPersonaId ? LASH_BROW_DEMO_PERSONAS.find(candidate => candidate.id === selectedPersonaId) ?? null : null;
  const handleDemoSelect = async (personaId: string) => {
    setSelectedPersonaId(personaId);
    setPhase("uploading");
    setUploadError(null);
    try {
      const persona_0 = LASH_BROW_DEMO_PERSONAS.find(candidate_0 => candidate_0.id === personaId);
      if (!persona_0) {
        throw new Error(`Demo persona is not configured: ${personaId}`);
      }
      const frontFile = await fetchDemoImageAsFile(persona_0.frontImageUrl, persona_0.frontFileName);
      const frontPhotoDataUrl = await readFileAsDataUrl(frontFile);
      const frontUploadResponse = await uploadFrontalFacialImageForAssessment({
        assessmentId: assessment.id,
        platformId: assessment.platformId,
        photoDataUrl: frontPhotoDataUrl,
        fileName: persona_0.frontFileName,
        requestOptions: {
          headers
        }
      });
      await setAssessmentPhotoCaptureComplete(assessment.id, {
        frontalFacialImageId: frontUploadResponse.imageId
      }, {
        headers
      });
      await queryClient.invalidateQueries({
        queryKey: getRetrieveCurrentAssessmentQueryKey()
      });
      analytics.trackEvent("assessment_photo_captured", {
        flowType
      });
      analytics.trackEvent("assessment_front_photo_upload_completed", {
        flowType
      });
      onComplete();
    } catch (error) {
      logger.error("lash_brow_demo_upload_failed", undefined, error as Error);
      setUploadError(t("assessmentSteps.studioSession.photoUpload.uploadFailed"));
      setPhase("error");
    }
  };
  const isUploading = phase === "uploading";
  return <div className="relative flex min-h-dvh w-full flex-1 flex-col overflow-hidden bg-black/90">
      <CaptureHeader />

      <div className="relative flex min-h-0 flex-1 items-start justify-center overflow-y-auto px-3 py-8 sm:items-center sm:px-6">
        <div className="relative w-full max-w-lg">
          <AnimatePresence mode="wait">
            {isUploading && selectedPersona ? <DemoUploadProgressPreview key="demo-uploading" frontImageUrl={selectedPersona.frontImageUrl} label={t("assessmentSteps.studioSession.photoUpload.uploading")} /> : <motion.div animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} initial={{
            opacity: 0,
            y: 12
          }} key="demo-picker" transition={{
            duration: 0.25
          }}>
                {uploadError ? <div className="mb-4 rounded-2xl bg-white/10 px-4 py-3 text-center">
                    <Text component="p" text={uploadError} tone="white" variant="body-sm" />
                  </div> : null}
                <div className="mb-2 text-center">
                  <Text component="p" text={t("assessmentSteps.studioSession.photoUpload.demoPairPrompt")} tone="white" variant="body-sm" weight="medium" />
                </div>
                <DemoPersonaSelector disabled={false} personas={demoThumbnails} onSelect={handleDemoSelect} />
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      <div className="pb-safe relative z-10 w-full px-4 py-6 sm:px-6" />
    </div>;
}
type DemoUploadProgressPreviewProps = {
  frontImageUrl: string;
  label: string;
};
function DemoUploadProgressPreview(t0) {
  const $ = _c(17);
  const {
    frontImageUrl,
    label
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 1
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      opacity: 0
    };
    t3 = {
      opacity: 0
    };
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  let t5;
  if ($[3] !== frontImageUrl) {
    t4 = <AppImage fill={true} alt="" className="object-cover opacity-40 blur-lg" src={frontImageUrl} />;
    t5 = <AppImage fill={true} alt="" className="object-cover" src={frontImageUrl} />;
    $[3] = frontImageUrl;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="absolute inset-0 bg-black/30" />;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-white" /></div>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t4 || $[9] !== t5) {
    t8 = <div className="relative aspect-3/4 w-2/3 overflow-hidden rounded-2xl bg-neutral-800">{t4}{t5}{t6}{t7}</div>;
    $[8] = t4;
    $[9] = t5;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Loader2 className="h-4 w-4 animate-spin" />;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== label) {
    t10 = <div className="bg-background/80 flex items-center gap-2 rounded-full px-4 py-2 shadow-lg backdrop-blur-sm">{t9}<Text component="span" text={label} variant="body-sm" weight="medium" /></div>;
    $[12] = label;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t10 || $[15] !== t8) {
    t11 = <motion.div animate={t1} className="mx-auto flex aspect-3/4 max-w-xs flex-col items-center justify-center gap-6 overflow-hidden rounded-3xl bg-neutral-900 px-4 py-8" exit={t2} initial={t3}>{t8}{t10}</motion.div>;
    $[14] = t10;
    $[15] = t8;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  return t11;
}
function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}